/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import {
  FeatureFlagEntry,
  STORAGE_KEY_PREFIX,
  getResolvedValue,
  isCockpitEnabled,
  registry,
} from './feature-flag-registry.core';

export type { FeatureFlagEntry } from './feature-flag-registry.core';
export {
  setFlagOverride,
  clearOverride,
  clearAllOverrides,
  getAllFlags,
  getNonMockFlags,
  getMockFlags,
} from './feature-flag-registry.overrides';

/**
 * Parses a string value to boolean with a default fallback.
 *
 * @param value - The string to parse
 * @param defaultValue - Fallback when value is empty or undefined
 * @returns The parsed boolean
 */
function parseBool(value: string | undefined, defaultValue: boolean): boolean {
  if (value === undefined || value === '') return defaultValue;
  const n = value.trim().toLowerCase();
  return n === 'true' || n === '1' || n === 'yes';
}

/**
 * Reads a feature flag override from sessionStorage.
 */
function readOverride(key: string): boolean | undefined {
  if (!isCockpitEnabled()) return undefined;
  try {
    const raw = sessionStorage.getItem(`${STORAGE_KEY_PREFIX}${key}`);
    if (raw === null) return undefined;
    return raw === 'true';
  } catch {
    return undefined;
  }
}

/**
 * Registers a feature flag, resolving its value from build-time env and default.
 *
 * Feature flags are resolved at BUILD TIME via Vite's import.meta.env.FF_* injection.
 * They are never read from runtime config (config.js / window.__ENV__) to prevent
 * exposing internal gating decisions in publicly-served static assets.
 *
 * @param key - Unique feature flag key (e.g. 'FF_BLOG')
 * @param envValue - Value from the build-time environment variable
 * @param defaultValue - Hardcoded default when env is absent
 * @param description - Human-readable description of the flag
 * @param options - Optional metadata (requiresDebug, isMockToggle, localOnly, parent).
 *   `parent` declares a dependency on another flag that does not follow the key
 *   prefix; the parent must be defined earlier in this module to cascade.
 * @returns The resolved boolean value of the flag
 */
export function defineFlag(
  key: string,
  envValue: string | undefined,
  defaultValue: boolean,
  description: string,
  options?: {
    requiresDebug?: boolean;
    isMockToggle?: boolean;
    localOnly?: boolean;
    parent?: string;
  },
): boolean {
  const buildValue = parseBool(envValue, defaultValue);
  const override = readOverride(key);

  const entry: FeatureFlagEntry = {
    key,
    description,
    buildValue,
    runtimeOverride: override,
    requiresDebug: options?.requiresDebug,
    isMockToggle: options?.isMockToggle,
    localOnly: options?.localOnly,
    parent: options?.parent,
  };

  registry.set(key, entry);

  return getResolvedValue(entry);
}

/**
 * Returns the current resolved value of a registered feature flag.
 *
 * @param key - The feature flag key to look up
 * @returns True if the flag is enabled, false otherwise
 */
export function getFlag(key: string): boolean {
  const entry = registry.get(key);
  if (!entry) return false;
  return getResolvedValue(entry);
}
